import React, { useState } from 'react';
import { Share2, Link as LinkIcon, Check, Facebook, Send } from 'lucide-react';
import { toast } from 'sonner';

// Resmi X ikonu (lucide-react Twitter kuş simgesini kaldırdı)
const XIcon = ({ className = 'w-3.5 h-3.5' }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const WhatsAppIcon = ({ className = 'w-3.5 h-3.5' }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M20.52 3.48A11.9 11.9 0 0 0 12.07 0C5.46 0 .07 5.4.07 12.02c0 2.12.56 4.19 1.62 6.02L0 24l6.08-1.6a11.98 11.98 0 0 0 5.99 1.6h.01c6.61 0 12-5.4 12-12.02 0-3.2-1.25-6.21-3.56-8.5zM12.08 21.9h-.01a9.86 9.86 0 0 1-5.03-1.38l-.36-.21-3.6.95.96-3.52-.23-.37a9.9 9.9 0 0 1-1.52-5.35c0-5.49 4.48-9.95 9.98-9.95 2.66 0 5.17 1.04 7.05 2.92a9.9 9.9 0 0 1 2.92 7.03c0 5.49-4.47 9.95-9.96 9.95zm5.46-7.45c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.76.97-.94 1.17-.17.2-.35.22-.65.08-.3-.15-1.26-.46-2.4-1.48-.89-.79-1.49-1.77-1.66-2.07-.17-.3-.02-.46.13-.6.14-.14.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.03-.52-.07-.15-.67-1.6-.92-2.19-.24-.58-.49-.5-.67-.5h-.57c-.2 0-.52.07-.8.37s-1.05 1.02-1.05 2.48c0 1.46 1.07 2.87 1.22 3.07.15.2 2.1 3.2 5.1 4.49.71.3 1.27.49 1.7.63.72.23 1.37.2 1.88.12.57-.08 1.76-.72 2.01-1.42.25-.7.25-1.3.17-1.42-.07-.12-.27-.2-.57-.35z" />
  </svg>
);

const TelegramIcon = ({ className = 'w-3.5 h-3.5' }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M22.265 2.428a1.5 1.5 0 0 0-1.52-.203L1.706 9.762a1.3 1.3 0 0 0 .064 2.44l4.42 1.397 2.37 7.32a1.21 1.21 0 0 0 2.04.39l2.39-2.564 4.52 3.312a1.5 1.5 0 0 0 2.36-.86L22.73 3.92a1.5 1.5 0 0 0-.466-1.492zM9.72 15.14l-.63 4.42-1.75-5.4 10.75-7.2z" />
  </svg>
);

export default function ShareButtons({ url, title = '', text = '', size = 'sm', className = '' }) {
  const [copied, setCopied] = useState(false);
  const fullUrl = url || (typeof window !== 'undefined' ? window.location.href : '');
  const shareTitle = title || (typeof document !== 'undefined' ? document.title : '');
  const shareText = text || shareTitle;

  const encUrl = encodeURIComponent(fullUrl);
  const encText = encodeURIComponent(shareText);
  const encTitle = encodeURIComponent(shareTitle);

  const links = {
    x: `https://twitter.com/intent/tweet?url=${encUrl}&text=${encText}`,
    whatsapp: `https://wa.me/?text=${encText}%20${encUrl}`,
    telegram: `https://t.me/share/url?url=${encUrl}&text=${encText}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encUrl}&quote=${encTitle}`,
  };

  const handleCopy = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(fullUrl);
      setCopied(true);
      toast.success('Bağlantı kopyalandı');
      setTimeout(() => setCopied(false), 1600);
    } catch {
      toast.error('Kopyalanamadı');
    }
  };

  const handleNative = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (navigator.share) {
      try {
        await navigator.share({ title: shareTitle, text: shareText, url: fullUrl });
      } catch { /* kullanıcı iptal etti */ }
    }
  };

  const iconSize = size === 'xs' ? 'w-3 h-3' : 'w-3.5 h-3.5';
  const btnPad = size === 'xs' ? 'p-1.5' : 'p-2';
  const canNative = typeof navigator !== 'undefined' && !!navigator.share;

  const btn = (href, label, color, Icon) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      className={`${btnPad} rounded-full ${color} transition-all hover:scale-110 shadow-sm`}
      aria-label={label}
      title={label}
      data-testid={`share-${label.toLowerCase()}`}
    >
      <Icon className={iconSize} />
    </a>
  );

  return (
    <div className={`flex items-center gap-1.5 ${className}`} data-testid="share-buttons" onClick={(e) => e.stopPropagation()}>
      {btn(links.x, 'X', 'bg-slate-900 text-white hover:bg-black', XIcon)}
      {btn(links.whatsapp, 'WhatsApp', 'bg-[#25D366] text-white hover:bg-[#1ebe57]', WhatsAppIcon)}
      {btn(links.telegram, 'Telegram', 'bg-[#229ED9] text-white hover:bg-[#1a86b8]', TelegramIcon)}
      {btn(links.facebook, 'Facebook', 'bg-[#1877F2] text-white hover:bg-[#145dbe]', Facebook)}
      <button
        type="button"
        onClick={handleCopy}
        className={`${btnPad} rounded-full bg-slate-100 text-slate-600 hover:bg-slate-200 transition-all hover:scale-110`}
        aria-label="Bağlantıyı kopyala"
        title="Bağlantıyı kopyala"
        data-testid="share-copy"
      >
        {copied ? <Check className={iconSize} /> : <LinkIcon className={iconSize} />}
      </button>
      {canNative && (
        <button
          type="button"
          onClick={handleNative}
          className={`${btnPad} rounded-full bg-emerald-500 text-white hover:bg-emerald-600 transition-all hover:scale-110 sm:hidden`}
          aria-label="Paylaş"
          title="Paylaş"
          data-testid="share-native"
        >
          <Share2 className={iconSize} />
        </button>
      )}
    </div>
  );
}
